import { DateTime } from 'luxon';
import moment from 'moment';
import { Platform } from 'react-native';
import { DayIntervalUnit } from '../graphql';

export const formatShortDate = (value) => {
  return DateTime.fromISO(value).toLocaleString(DateTime.DATE_SHORT);
};

export const formatShortDateAndTime = (value) => {
  const date = DateTime.fromISO(value);
  return getDateAndTime(date);
};

export const formatShortDateTimeOrSameDay = (value) => {
  const dateTime = DateTime.fromISO(value);

  return isSameDay(dateTime)
    ? dateTime.toLocaleString(DateTime.TIME_SIMPLE)
    : dateTime.toLocaleString(DateTime.DATETIME_SHORT);
};

export const formatShortDateOrSameDay = (value) => {
  const dateTime = DateTime.fromISO(value);

  return isSameDay(dateTime)
    ? dateTime.toLocaleString(DateTime.TIME_SIMPLE)
    : formatShortDate(dateTime);
};

export const formatShortDateAndTimeOrSameDay = (value) => {
  const dateTime = DateTime.fromISO(value);

  return isSameDay(dateTime)
    ? dateTime.toLocaleString(DateTime.TIME_SIMPLE)
    : formatShortDateAndTime(dateTime);
};

const getDateAndTime = (dateTime) => {
  return `${dateTime.toLocaleString(
    DateTime.DATE_SHORT
  )}  ${dateTime.toLocaleString(DateTime.TIME_SIMPLE)}`;
};

const isSameDay = (dateTime) => {
  const today = DateTime.now();
  return dateTime.hasSame(today, 'day');
};

export const DATE_FORMAT_MMDDYYYY = 'MM/DD/YYYY';
export const DATE_FORMAT_MMDDYYYYLUXON = 'MM/dd/yyyy';
export const TIME_FORMAT_HHMM = 'HH:mm';

export const getYearBounds = () => {
  const firstDay = moment(`2023-01-01`).startOf('day').toDate();
  const lastDay = moment(`2050-12-31`).endOf('day').toDate();

  return { firstDay, lastDay };
};

// from DateSelector "MM/DD/YYYY" -> ISO String "YYYY-MM-DD"
export const convertToISO = (dateString?: string): string | undefined => {
  if (!dateString) return undefined;

  const dt = DateTime.fromFormat(dateString, 'MM/dd/yyyy');
  if (!dt.isValid) return undefined;

  return dt.toISODate(); // "YYYY-MM-DD"
};

export const parseMMDDYYYYToDate = (dateString: string): Date | undefined => {
  if (!dateString) return undefined;

  const dt = DateTime.fromFormat(dateString, 'MM/dd/yyyy');
  return dt.isValid ? dt.toJSDate() : undefined;
};

// Date selector in alertForm "YYYY-MM-DD" -> "MM/DD/YYYY"
export const formatDateToMMDDYYYY = (
  dateInput: string | Date | undefined
): string | undefined => {
  if (!dateInput) return undefined;

  let dateTime: DateTime;

  if (typeof dateInput === 'string') {
    dateTime = DateTime.fromISO(dateInput);
  } else {
    dateTime = DateTime.fromJSDate(dateInput);
  }

  if (!dateTime.isValid) {
    console.error('Invalid date input:', dateInput);
    return undefined;
  }

  return dateTime.toFormat('MM/dd/yyyy');
};

export const getModeFormatDate = (mode?: 'date' | 'time' | 'datetime') => {
  switch (mode) {
    case 'date':
      return DATE_FORMAT_MMDDYYYY;
    case 'time':
      return `${TIME_FORMAT_HHMM}`;
    case 'datetime':
      return `${DATE_FORMAT_MMDDYYYY} ${TIME_FORMAT_HHMM}`;
    default:
      return DATE_FORMAT_MMDDYYYY;
  }
};

export const getLocalTime = (dateUTC: string) => {
  if (!isValidIsoString(dateUTC)) return '';

  const date = new Date(dateUTC);

  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: Platform.OS !== 'web',
  });
};
export const formatDateLocalized = (dateUTC: string) => {
  const date = new Date(dateUTC);
  const timeZone = DateTime.local();
  let timeZoneLabel = DateTime.local().offsetNameShort;

  if (Platform.OS === 'ios') {
    timeZoneLabel = `${timeZoneLabel}${timeZone.toFormat('Z')}`;
  }

  return `${date.toLocaleString(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  })} ${timeZoneLabel}`;
};

export const getUTCDateFromTime = (time: string): string => {
  if (time === '') return '';
  const [hours, minutes] = time.split(':').map(Number);
  const dateLocal = moment().set({
    hour: hours,
    minute: minutes,
    second: 0,
    millisecond: 0,
  });

  return dateLocal.utc().toISOString();
};

export const isAfterDay = (start: string, end: string) => {
  const startDate = moment(start);

  const endDate = moment(end);
  return startDate.isAfter(endDate);
};

export const getCSTFormUTC = (utcIso: string) => {
  const date = new Date(utcIso);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

export const isValidIsoString = (val: unknown): val is string => {
  if (typeof val !== 'string') return false;

  const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/;

  if (!isoRegex.test(val)) return false;

  return !isNaN(Date.parse(val));
};

/**
 * Gets the effective creation date for an issue.
 * Returns adjustCreationDate if set, otherwise falls back to createdAt.
 * @param adjustCreationDate - The adjusted creation date (optional)
 * @param createdAt - The original creation date
 * @returns The effective creation date string
 */
export const getEffectiveCreationDate = (
  adjustCreationDate: string | null | undefined,
  createdAt: string
): string => {
  return adjustCreationDate || createdAt;
};  
  
export const calculateMinutesDifference = (futureDate: string | Date, currentDate: string | Date) => {
  const future = new Date(futureDate);
  const current = new Date(currentDate);
  
  const diffTime = future.getTime() - current.getTime();
  const diffMinutes = Math.floor(diffTime / (1000 * 60));

  return diffMinutes;
};

export const calculateHoursDifference = (futureDate: string | Date, currentDate: string | Date) => {
  const future = new Date(futureDate);
  const current = new Date(currentDate);
  const diffTime = future.getTime() - current.getTime();
  const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
  return diffHours;
};

export const calculateDaysDifference = (futureDate: string | Date, currentDate: string | Date) => {
  const future = new Date(futureDate);
  future.setHours(0, 0, 0, 0);
  
  const current = new Date(currentDate);
  current.setHours(0, 0, 0, 0);
  
  const diffTime = future.getTime() - current.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};

/**
 * Calculates the date from a given interval.
 * 
 * @param dayInterval - The number of the interval
 * @param dayIntervalUnit - The unit of the interval (minutes, hours, days, weeks, months)
 * @param startDate - The starting date (optional, defaults to current date)
 * @returns New date after adding the interval, or original day if the input is invalid
 */
export const calculateDateFromInterval = (dayInterval: number, dayIntervalUnit: DayIntervalUnit, startDate?: Date) => {
  let adjustedDate = startDate ? startDate : new Date();
  adjustedDate = typeof adjustedDate === 'string' ? new Date(adjustedDate) : adjustedDate;

  if (dayInterval && dayIntervalUnit) {
    switch (dayIntervalUnit) {
      case DayIntervalUnit.Minutes:
        adjustedDate = moment(adjustedDate).add(dayInterval, 'minutes');
        break;
      case DayIntervalUnit.Hours:
        adjustedDate = moment(adjustedDate).add(dayInterval, 'hours');
        break;
      case DayIntervalUnit.Days:
        adjustedDate = moment(adjustedDate).add(dayInterval, 'days');
        break;
      case DayIntervalUnit.Weeks:
        adjustedDate = moment(adjustedDate).add(dayInterval, 'weeks');
        break;
      case DayIntervalUnit.Months:
        adjustedDate = moment(adjustedDate).add(dayInterval, 'months');
        break;
    }
  }

  return adjustedDate;
};


/**
 * Calculates the number of days from a given interval by dividing the days value by the interval unit.
 * 
 * @param daysValue - The number of days
 * @param dayIntervalUnit - The unit of the interval (days, weeks, months)
 * @returns The calculated number of days, or original day if the input is invalid
 */
export const convertDaysToIntervalDays = (dayIntervalUnit: DayIntervalUnit, daysValue: number) => {
    if (!daysValue) {
      return undefined;
    }

    if (!dayIntervalUnit) {
      return daysValue;
    }

    let daysInInterval = daysValue;

    switch (dayIntervalUnit) {
      case DayIntervalUnit.Days:
        daysInInterval = daysValue;
        break;
      case DayIntervalUnit.Weeks:
        daysInInterval = daysValue / 7;
        break;
      case DayIntervalUnit.Months:
        daysInInterval = daysValue / 30;
        break;
    }

    return Math.floor(daysInInterval);
};